import PhotoSphere from "./PhotoSphere";
import * as TWEEN from "@tweenjs/tween.js";
import Scene from "./Scene";

/**
 * Represents a 360 scene within the player
 */
export default class Scene3D extends Scene {
  /**
   * Takes an image config object and creates the initial 360 scene
   *
   * @example
   * {
   *   url: '/example.jpg',
   *   hotspots: [
   *     {
   *       u: 0,
   *       v: 0.5,
   *       type: 'web',
   *       options: {
   *         url : 'http://google.com'
   *       }
   *     }
   *   ]
   * }
   *
   */
  constructor(player, image, onFullyLoaded = () => {}) {
    super(
      player,
      image,
      _this => {
        // Load photosphere
        _this._photoHolder = new PhotoSphere(
          player,
          {
            urls: image.urls,
          },
          full => {
            // Called when photosphere loads
            if (full) player?.onEndImageLoad?.();
            _this.imageLoaded = true;
          },
          promise => {
            _this.loadedPromise = promise;
          },
          { half: "top", isStereo: true },
        );
        _this._photoHolder.layers.set(1);
        _this.group.add(_this._photoHolder);

        _this._photoHolderRight = new PhotoSphere(
          player,
          {
            urls: image.urls,
          },
          full => {
            // Called when photosphere loads
            if (full) player?.onEndImageLoad?.();
            _this.imageLoaded = true;
          },
          promise => {
            _this.loadedPromise = promise;
          },
          { half: "bottom", isStereo: true },
        );
        _this._photoHolderRight.layers.set(2);
        _this.group.add(_this._photoHolderRight);

        _this.setRotation();
      },
      onFullyLoaded,
        "Scene3D"
    );
    this.fullyLoaded = false;
  }

  async loadFullRes() {
    // Make sure the textures are set at the same time.
    Promise.all([
      this._photoHolder.getCurrentTexture("medium"),
      this._photoHolderRight.getCurrentTexture("medium"),
    ]).then(medium => {
      if (!this.isFullyLoaded()) {
        this._photoHolder.setTexture(medium[0]);
        this._photoHolderRight.setTexture(medium[1]);
      }
    });

    Promise.all([
      this._photoHolder.getCurrentTexture("high"),
      this._photoHolderRight.getCurrentTexture("high"),
    ]).then(high => {
      this._photoHolder.setTexture(high[0]);
      this._photoHolderRight.setTexture(high[1]);
      if (this.onFullyLoaded) {
        this.onFullyLoaded();
      }
    });
    this._player?.onEndImageLoad?.();
  }

  setRotation() {
    this.group.rotation.y = -Math.PI / 2;
    if (this._image.horizonOffsetX) {
      this._photoHolder.rotation.x =
        (-180 - this._image.horizonOffsetX) * (Math.PI / 180);
      this._photoHolderRight.rotation.x =
        (-180 - this._image.horizonOffsetX) * (Math.PI / 180);
    }
    if (this._image.horizonOffsetZ) {
      this._photoHolder.rotation.z =
        (-180 - this._image.horizonOffsetZ) * (Math.PI / 180);
      this._photoHolderRight.rotation.z =
        (-180 - this._image.horizonOffsetZ) * (Math.PI / 180);
    }
    this._startingRotation = undefined;
    if (this._image.startingRotation) {
      this.group.rotation.y -= this._image.startingRotation.y;
      this._startingRotation = this._image.startingRotation;
    }
  }

  updateHorizonOffset(offsetInDegrees) {
    // Convert to radians and update
    this.setRotation();
    if (offsetInDegrees.x) {
      this._photoHolder.rotation.x =
        (-180 - offsetInDegrees.x) * (Math.PI / 180);
      this._photoHolderRight.rotation.x =
        (-180 - offsetInDegrees.x) * (Math.PI / 180);
    }
    if (offsetInDegrees.z) {
      this._photoHolder.rotation.z =
        (-180 - offsetInDegrees.z) * (Math.PI / 180);
      this._photoHolderRight.rotation.z =
        (-180 - offsetInDegrees.z) * (Math.PI / 180);
    }
  }

  transition(
    photosphere,
    targetPosition,
    transitionSpeed,
    startPosition,
    easing,
  ) {
    let position = {
      x: photosphere.position.x,
      y: photosphere.position.y,
      z: photosphere.position.z,
    };

    if (startPosition !== undefined) {
      position = startPosition;
    }

    const tween = new TWEEN.Tween(position).to(targetPosition, transitionSpeed);
    tween.easing(TWEEN.Easing.Quadratic[easing]);
    tween.onUpdate(() => {
      if (!photosphere) {
        console.warn("No photosphere when trying to transition");
        return;
      }
      photosphere.position.x = position.x;
      photosphere.position.y = position.y;
      photosphere.position.z = position.z;
    });

    return tween;
  }

  dispose() {
    // Remove Scene3D-specific resources before calling super.dispose()
    // which sets this.group = null
    if (this._photoHolder) {
      this._photoHolder.dispose();
      if (this.group) {
        this.group.remove(this._photoHolder);
      }
    }
    if (this._photoHolderRight) {
      this._photoHolderRight.dispose();
      if (this.group) {
        this.group.remove(this._photoHolderRight);
      }
    }
    super.dispose();
  }

  onTransitionCompleteDispose() {
    try {
      this.dispose();
    } catch (err) {
      console.warn(err);
    }
    this._transitioning = false;
  }

  onTransitionComplete() {
    this._transitioning = false;
    this.loadFullRes();
  }

  startTransition(
    targetPosition,
    transitionSpeed,
    startPosition,
    easing,
    dispose,
  ) {
    this._transitioning = true;
    for (let h = 0; h < this._hotspots.length; h++) {
      this._hotspots[h].setHover(false);
    }

    const t1 = this.transition(
      this._photoHolder,
      targetPosition,
      transitionSpeed,
      startPosition,
      easing,
    );
    const t2 = this.transition(
      this._photoHolderRight,
      targetPosition,
      transitionSpeed,
      startPosition,
      easing,
    );

    if (dispose) {
      t1.onComplete(() => {
        this.onTransitionCompleteDispose();
      });
    } else {
      t1.onComplete(() => {
        this.onTransitionComplete();
      });
    }

    t1.onStart(() => {
      t2.start();
    });
    return t1;
  }
}
